/**
 * Sidebar module drag-reorder hook. Extracted verbatim (behavior-preserving)
 * from layout/Sidebar.tsx — owns the transient `moduleDragIndex` and the three
 * module DnD handlers (`handleModuleDragStart`, `handleModuleDragOver`,
 * `handleModuleDragEnd`).
 *
 * STATE OWNERSHIP (deliberate): `tempModuleOrder` / `setTempModuleOrder` stay in
 * the orchestrator because the `filteredModules` memo is computed FROM
 * `tempModuleOrder` (`computeFilteredModules({ tempModuleOrder, ... })`) and the
 * drag handlers in turn read `filteredModules` — a cycle. The state therefore
 * must be declared before that memo, while this hook must be called AFTER it to
 * receive `filteredModules`. So only `moduleDragIndex` (read solely inside these
 * handlers) is owned here; `tempModuleOrder` is threaded in. Every handler body
 * is byte-identical to the original; `handleModuleDragEnd` keeps its exact
 * `useCallback` dependency array `[tempModuleOrder, workspaceId, token,
 * queryClient]`.
 *
 * The live splice-reorder is delegated to `reorderByIndex` and the
 * position-diff persistence list to `computeModulePositionUpdates` (verbatim
 * ports pinned by dragReorder.test.ts).
 */

import type React from 'react';
import { useCallback, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { reorderByIndex, computeModulePositionUpdates } from '../dragReorder';
import type { SidebarModuleItem } from '../types';

interface UseModuleDragReorderArgs {
  filteredModules: SidebarModuleItem[];
  tempModuleOrder: SidebarModuleItem[] | null;
  setTempModuleOrder: (value: SidebarModuleItem[] | null) => void;
  workspaceId: string;
  token: string | null | undefined;
  queryClient: QueryClient;
}

export function useModuleDragReorder({
  filteredModules,
  tempModuleOrder,
  setTempModuleOrder,
  workspaceId,
  token,
  queryClient,
}: UseModuleDragReorderArgs) {
  const [moduleDragIndex, setModuleDragIndex] = useState<number | null>(null);

  const handleModuleDragStart = (e: React.DragEvent, item: SidebarModuleItem) => {
    const items = tempModuleOrder ?? filteredModules;
    const idx = items.findIndex((i) => i.module.id === item.module.id);
    setModuleDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e: React.DragEvent, item: SidebarModuleItem) => {
    e.preventDefault();
    const items = tempModuleOrder ?? filteredModules;
    const targetIdx = items.findIndex((i) => i.module.id === item.module.id);
    if (moduleDragIndex === null || moduleDragIndex === targetIdx) return;
    const newOrder = reorderByIndex(items, moduleDragIndex, targetIdx);
    setTempModuleOrder(newOrder);
    setModuleDragIndex(targetIdx);
  };

  const handleModuleDragEnd = useCallback(async () => {
    setModuleDragIndex(null);
    if (!tempModuleOrder || !workspaceId || !token) return;
    // Persist only modules whose position actually changed
    try {
      const updates = computeModulePositionUpdates(tempModuleOrder);
      await Promise.all(
        updates.map(({ mod, newPos }) =>
          api.workspaces.updateWorkspaceModule(token, workspaceId, mod.module.id, { position: newPos })
        )
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) {
      console.error('[ConversationSidebar] Failed to persist module order:', err);
    }
    setTempModuleOrder(null);
  }, [tempModuleOrder, workspaceId, token, queryClient]);

  return {
    moduleDragIndex,
    handleModuleDragStart,
    handleModuleDragOver,
    handleModuleDragEnd,
  };
}
